import type { TextGenApi } from '../textGen/TextGenApi'
import type { ImageGenApi } from '../imageGen/ImageGenApi'
import type { AudioGenApi } from '../audioGen/AudioGenApi'
import type { SpriteGenApi } from '../spriteGen/SpriteGenApi'
import type { ThreeDGenApi } from '../threeDGen/ThreeDGenApi'
import type { UgcApi } from '../ugc/UgcApi'
import type { RoomsApi } from '../rooms/RoomsApi'
import type { SimulationApi } from '../simulation/SimulationApi'
import type { MultiplayerApi } from '../mp/client/MultiplayerApi'
import type { AccessGateApi } from './AccessGateApi'
import { createAccessGatedApi } from './createAccessGatedApi'
import type { AccessGateConfig } from './createAccessGatedApi'
import { gateStreaming } from './gateStreaming'
import type {
  AiChatCompletionRequest,
  AiChatCompletionStreamOptions,
  PromptCompletionRequest,
} from '../ai/AiApi'

export interface GatableHost {
  accessGate: AccessGateApi
  textGen: TextGenApi
  imageGen: ImageGenApi
  audioGen: AudioGenApi
  spriteGen: SpriteGenApi
  threeDGen: ThreeDGenApi
  ugc: UgcApi
  rooms: RoomsApi
  simulation: SimulationApi
  multiplayer: MultiplayerApi
  /** Per-game guest-multiplayer exemption (RemoteHost: injected static
   *  config). Absent/false keeps every multiplayer method gated. Advisory —
   *  POST /tickets re-checks server-side. */
  anonymousMultiplayerAllowed?: () => boolean
}

// Keep total over MultiplayerApi: an undeclared new method must stay a compile
// error, and every method must declare whether the per-game guest exemption
// applies. 'ticket-minting' methods funnel through POST /tickets (server
// re-checks anonymity there); 'listing' never mints a ticket (client-side
// Firestore query), so its only anonymity check is this gate + the
// rundot_rooms rule — it is never exempted.
export const GATED_MULTIPLAYER_METHODS: Record<keyof MultiplayerApi, 'ticket-minting' | 'listing'> = {
  createRoom: 'ticket-minting',
  joinOrCreateRoom: 'ticket-minting',
  joinRoomByCode: 'ticket-minting',
  matchmakeRoom: 'ticket-minting',
  getUserRooms: 'listing',
}

// Keep total over ImageGenApi, for the same reason as the multiplayer record
// above: a new method must not reach players ungated by omission. Every
// model-invoking method is 'gated' — it bills creator credits and accepts
// arbitrary image input, so it needs a real signed-in identity.
//
// The two 'ungated' entries are ungated for the same reason, not by oversight:
// neither spends nor takes caller input. `getCompletedJobs` only polls results
// of calls that already passed the gate, and `listModels` is unbilled
// discovery — gating it would mean a game cannot ask which models it may run
// until the player signs in, which is the opposite of what discovery is for.
export const GATED_IMAGE_GEN_METHODS: Record<keyof ImageGenApi, 'gated' | 'ungated'> = {
  generate: 'gated',
  estimateDepth: 'gated',
  removeBackground: 'gated',
  upscaleImage: 'gated',
  listModels: 'ungated',
  getCompletedJobs: 'ungated',
}

/**
 * Wraps gated API methods on a host with access-gate checks.
 * Call this once during host construction, after all API instances
 * and the accessGate have been assigned.
 */
export function applyAccessGates(host: GatableHost): void {
  const gateConfig: AccessGateConfig = {
    isAnonymous: () => host.accessGate.isAnonymous(),
    promptLogin: () => host.accessGate.promptLogin(),
    isAutoPromptEnabled: () => host.accessGate.autoPromptLogin,
  }

  // `requestChatCompletionStreamAsync` can't go through `createAccessGatedApi`
  // (it wraps return values as Promises, breaking the `AsyncIterable<chunk>`
  // contract). Instead `gateStreaming` returns an AsyncIterable that runs the
  // access check on first iteration — prompting BEFORE the stream START, so an
  // anonymous user signs in mid-session and the stream opens authenticated.
  const baseTextGen = host.textGen
  const gatedStream = gateStreaming(
    (request: AiChatCompletionRequest, options?: AiChatCompletionStreamOptions) =>
      baseTextGen.requestChatCompletionStreamAsync(request, options),
    gateConfig,
  )
  const gatedPromptStream = gateStreaming(
    (request: PromptCompletionRequest, options?: AiChatCompletionStreamOptions) =>
      baseTextGen.requestPromptCompletionStreamAsync(request, options),
    gateConfig,
  )
  const gatedTextGen = createAccessGatedApi(
    baseTextGen,
    ['requestChatCompletionAsync', 'requestPromptCompletionAsync', 'getAvailableCompletionModels'],
    'authenticated_18plus',
    gateConfig,
  )
  host.textGen = new Proxy(gatedTextGen, {
    get(target, prop, receiver) {
      if (prop === 'requestChatCompletionStreamAsync') {
        return gatedStream
      }
      if (prop === 'requestPromptCompletionStreamAsync') {
        return gatedPromptStream
      }
      return Reflect.get(target, prop, receiver)
    },
  })

  // 'ungated' methods are left out of the wrap entirely — they stay in the map
  // so the totality check still forces a decision on every method.
  const imageGenKeys = Object.keys(GATED_IMAGE_GEN_METHODS) as (keyof ImageGenApi)[]
  host.imageGen = createAccessGatedApi(
    host.imageGen,
    imageGenKeys.filter((k) => GATED_IMAGE_GEN_METHODS[k] === 'gated'),
    'authenticated_18plus',
    gateConfig,
  )

  host.audioGen = createAccessGatedApi(
    host.audioGen,
    ['generate', 'designVoices', 'saveDesignedVoice'],
    'authenticated_18plus',
    gateConfig,
  )

  host.spriteGen = createAccessGatedApi(
    host.spriteGen,
    ['generate', 'animate'],
    'authenticated_18plus',
    gateConfig,
  )

  host.threeDGen = createAccessGatedApi(
    host.threeDGen,
    ['generate', 'remesh', 'rig', 'animate'],
    'authenticated_18plus',
    gateConfig,
  )

  host.ugc = createAccessGatedApi(
    host.ugc,
    ['create', 'update', 'delete', 'like', 'unlike', 'report'],
    'authenticated_18plus',
    gateConfig,
  )

  host.rooms = createAccessGatedApi(
    host.rooms,
    [
      'createRoomAsync',
      'joinOrCreateRoomAsync',
      'joinRoomByCodeAsync',
      'getUserRoomsAsync',
      'subscribeAsync',
      'updateRoomDataAsync',
      'getRoomDataAsync',
      'sendRoomMessageAsync',
      'leaveRoomAsync',
      'kickPlayerAsync',
      'startRoomGameAsync',
      'proposeMoveAsync',
      'validateMoveAsync',
    ],
    'authenticated_18plus',
    gateConfig,
  )

  // Realtime multiplayer entry points. `send` and `leave` live on the
  // returned `ServerRoom` instance, which is only obtainable through one
  // of these gated entry points, so they inherit the gate transitively.
  const multiplayerKeys = Object.keys(GATED_MULTIPLAYER_METHODS) as (keyof MultiplayerApi)[]
  const ticketMinting = multiplayerKeys.filter((k) => GATED_MULTIPLAYER_METHODS[k] === 'ticket-minting')
  const listing = multiplayerKeys.filter((k) => GATED_MULTIPLAYER_METHODS[k] === 'listing')
  const multiplayerGateConfig: AccessGateConfig = {
    ...gateConfig,
    isAnonymous: () => gateConfig.isAnonymous() && !(host.anonymousMultiplayerAllowed?.() ?? false),
  }
  host.multiplayer = createAccessGatedApi(host.multiplayer, ticketMinting, 'authenticated_18plus', multiplayerGateConfig)
  host.multiplayer = createAccessGatedApi(host.multiplayer, listing, 'authenticated_18plus', gateConfig)
}
